import { AxiosInstance } from 'axios';
import { createApiClient } from '../http/client';
import { ApiResponse } from '../../interfaces/common';
import { CallRoute, CreateCallRouteRequest, UpdateCallRouteRequest } from '../../interfaces/calls';
import { AuthService } from '../auth';

const APPOINTMENTS_API_BASE_URL = process.env.REACT_APP_PUBLIC_APPOINTMENTS_API_BASE_URL;

class QueuesService {
  private axios: AxiosInstance;

  constructor() {
    this.axios = createApiClient({
      baseURL: APPOINTMENTS_API_BASE_URL,
    });
  }

  private getAuthHeaders() {
    return {
      Authorization: `Bearer ${AuthService.getAuth()?.auth_token}`,
    };
  }

  async getAllRoutes(): Promise<ApiResponse<CallRoute[]>> {
    try {
      const response = await this.axios.get<CallRoute[]>('/communications/queues', {
        headers: this.getAuthHeaders(),
      });
      return {
        success: true,
        statusCode: 200,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Error fetching routes:', error);
      return {
        success: false,
        statusCode: error.response?.status || 500,
        message: error.response?.data?.message || 'Failed to fetch routes',
      };
    }
  }

  async getRoute(id: number): Promise<ApiResponse<CallRoute>> {
    try {
      const response = await this.axios.get<CallRoute>(`/communications/queues/${id}`, {
        headers: this.getAuthHeaders(),
      });
      return {
        success: true,
        statusCode: 200,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Error fetching route:', error);
      return {
        success: false,
        statusCode: error.response?.status || 500,
        message: error.response?.data?.message || 'Failed to fetch route',
      };
    }
  }

  async createRoute(data: CreateCallRouteRequest): Promise<ApiResponse<CallRoute>> {
    try {
      const response = await this.axios.post<CallRoute>('/communications/queues', data, {
        headers: this.getAuthHeaders(),
      });
      return {
        success: true,
        statusCode: 201,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Error creating route:', error);
      return {
        success: false,
        statusCode: error.response?.status || 500,
        message: error.response?.data?.message || 'Failed to create route',
      };
    }
  }

  async updateRoute(id: number, data: UpdateCallRouteRequest): Promise<ApiResponse<CallRoute>> {
    try {
      const response = await this.axios.put<CallRoute>(`/communications/queues/${id}`, data, {
        headers: this.getAuthHeaders(),
      });
      return {
        success: true,
        statusCode: 200,
        data: response.data,
      };
    } catch (error: any) {
      console.error('Error updating route:', error);
      return {
        success: false,
        statusCode: error.response?.status || 500,
        message: error.response?.data?.message || 'Failed to update route',
      };
    }
  }

  async deleteRoute(id: number): Promise<ApiResponse<void>> {
    try {
      await this.axios.delete(`/communications/queues/${id}`, {
        headers: this.getAuthHeaders(),
      });
      return {
        success: true,
        statusCode: 200,
      };
    } catch (error: any) {
      console.error('Error deleting route:', error);
      return {
        success: false,
        statusCode: error.response?.status || 500,
        message: error.response?.data?.message || 'Failed to delete route',
      };
    }
  }
}

export default new QueuesService();
